// src/csv.js

/**
 * Minimal CSV parser that supports:
 * - Header row (first line) -> returns array of objects
 * - Quoted fields with commas and double quotes
 * - Newlines within quoted fields
 *
 * @param {string} text
 * @returns {{headers: string[], rows: object[], raw: string[][]}}
 */
export function parseCSV(text) {
  if (typeof text !== "string") return { headers: [], rows: [], raw: [] };

  // Tokenize CSV into rows of fields using a small state machine.
  const rows = [];
  let row = [];
  let field = "";
  let i = 0;
  let inQuotes = false;

  while (i < text.length) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === '"') {
        // Possible escaped quote
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        } else {
          inQuotes = false;
          i += 1;
          continue;
        }
      } else {
        field += ch;
        i += 1;
        continue;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
        i += 1;
        continue;
      }
      if (ch === ",") {
        row.push(field);
        field = "";
        i += 1;
        continue;
      }
      if (ch === "\r") {
        // ignore CR; normalize on LF
        i += 1;
        continue;
      }
      if (ch === "\n") {
        row.push(field);
        rows.push(row);
        row = [];
        field = "";
        i += 1;
        continue;
      }
      field += ch;
      i += 1;
    }
  }
  // push last field/row
  row.push(field);
  rows.push(row);

  // Trim possible trailing empty row from ending newline
  if (rows.length && rows[rows.length - 1].length === 1 && rows[rows.length - 1][0] === "") {
    rows.pop();
  }

  if (rows.length === 0) return { headers: [], rows: [], raw: [] };

  const headers = rows[0].map((h) => String(h || "").trim());
  const dataRows = rows.slice(1);

  const objects = dataRows.map((r) => {
    const obj = {};
    headers.forEach((h, idx) => {
      obj[h] = r[idx] ?? "";
    });
    return obj;
  });

  return { headers, rows: objects, raw: rows };
}

/**
 * Convert an array of objects (or array of arrays) to CSV string.
 * @param {object[]|any[][]} data
 * @param {string[]} [headers] If omitted for object array, uses keys from first object.
 * @returns {string}
 */
export function toCSV(data, headers) {
  if (!Array.isArray(data) || data.length === 0) return "";

  let rows = [];

  if (Array.isArray(data[0])) {
    // Array of arrays
    rows = data;
  } else {
    // Array of objects
    const inferred = headers && headers.length ? headers : Array.from(
      new Set(data.flatMap((o) => Object.keys(o)))
    );
    rows.push(inferred);
    for (const obj of data) {
      rows.push(inferred.map((h) => obj[h]));
    }
  }

  const escape = (val) => {
    if (val == null) val = "";
    val = String(val);
    // Quote if contains comma, quote, or newline
    if (/[",\n]/.test(val)) {
      return `"${val.replace(/"/g, '""')}"`;
    }
    return val;
  };

  return rows.map((r) => r.map(escape).join(",")).join("\n");
}

/**
 * Trigger a CSV file download in the browser.
 * (No-op on server; intended for client usage.)
 * @param {string} filename
 * @param {object[]|any[][]} data
 * @param {string[]} [headers]
 */
export function downloadCSV(filename, data, headers) {
  try {
    const csv = toCSV(data, headers);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "data.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    // swallow in non-browser environments
    console.error("downloadCSV failed:", e);
  }
}

// Default export for files that do `import csv from './csv'`
export const downloadCsv = downloadCSV;
const csv = { parseCSV, toCSV, downloadCSV };
export default csv;